'use client';

import { Upload } from '@shared/icons';
import { useTranslations } from 'next-intl';
import { useCallback, useRef, useState } from 'react';
import type { BaseDragEvent, DropEventLike } from '../types';
import { ACCEPTED_FILE_TYPES, MAX_FILES, MAX_FILE_SIZE_MB } from '../utils/validateFile';

const processDragEvent = ({ event, isActive, setDragOver }: { event: BaseDragEvent; isActive: boolean; setDragOver: (value: boolean) => void }) => {
  event.preventDefault();
  event.stopPropagation();
  setDragOver(isActive);
};

const processDropEvent = ({
  event,
  disabled,
  handleFileSelect,
  setDragOver,
}: {
  event: DropEventLike;
  disabled: boolean;
  handleFileSelect: (files: FileList | null) => void;
  setDragOver: (value: boolean) => void;
}) => {
  processDragEvent({ event, isActive: false, setDragOver });

  if (disabled) return;

  const files = event.dataTransfer.files;
  handleFileSelect(files);
};

type FileDropZoneProps = {
  onFilesSelected: (files: File[]) => void;
  disabled: boolean;
  currentFileCount: number;
};

function FileDropZone({ onFilesSelected, disabled, currentFileCount }: FileDropZoneProps) {
  const t = useTranslations('global_dialogs.kyc_verification.manual_upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const remainingSlots = MAX_FILES - currentFileCount;
      const filesToProcess = Array.from(files).slice(0, remainingSlots);

      if (filesToProcess.length > 0) {
        onFilesSelected(filesToProcess);
      }
    },
    [onFilesSelected, currentFileCount]
  );

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(event.target.files);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    processDragEvent({ event, isActive: true, setDragOver: setIsDragOver });
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    processDragEvent({ event, isActive: true, setDragOver: setIsDragOver });
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLDivElement>) => {
    processDragEvent({ event, isActive: false, setDragOver: setIsDragOver });
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      processDropEvent({
        event,
        disabled,
        handleFileSelect,
        setDragOver: setIsDragOver,
      });
    },
    [disabled, handleFileSelect]
  );

  const acceptedTypes = ACCEPTED_FILE_TYPES.join(',');
  const isMaxFilesReached = currentFileCount >= MAX_FILES;

  return (
    <div className="flex flex-col gap-6">
      <div
        className={`flex flex-col items-center justify-center gap-4 p-6 rounded-lg border-2 border-dashed transition-colors ${
          disabled
            ? 'border-border/50 bg-background-inverse/5 cursor-not-allowed opacity-60'
            : isDragOver
              ? 'border-primary bg-primary/5 cursor-pointer'
              : 'border-border bg-background-inverse/5 cursor-pointer'
        }`}
        data-testid="file-drop-zone"
        data-dragging={isDragOver}
        onDragOver={!disabled ? handleDragOver : undefined}
        onDragEnter={!disabled ? handleDragEnter : undefined}
        onDragLeave={!disabled ? handleDragLeave : undefined}
        onDrop={!disabled ? handleDrop : undefined}
        onClick={!disabled ? handleButtonClick : undefined}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={
          !disabled
            ? e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleButtonClick();
                }
              }
            : undefined
        }
        aria-disabled={disabled}
        data-track-container="Upload"
      >
        <div className="flex flex-col items-center gap-3">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${disabled ? 'bg-muted/20' : 'bg-foreground/10'}`}>
            <Upload size="extra-small" className={disabled ? 'text-muted-foreground' : 'text-foreground'} />
          </div>
          <div className="flex flex-col items-center gap-2">
            <p className="paragraph-s-strong">{t('drop_zone_title')}</p>
            <p className="paragraph-s">{`File format ${ACCEPTED_FILE_TYPES.join(', ')}. Max size ${MAX_FILE_SIZE_MB}MB`}</p>
            {isMaxFilesReached && (
              <p data-testid="max-files-message" className="text-xs">
                {t('max_files_reached', { count: MAX_FILES })}
              </p>
            )}
          </div>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={acceptedTypes}
          multiple
          onChange={handleFileInputChange}
          data-testid="file-input"
          aria-label={t('upload_button')}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

export { FileDropZone, processDragEvent, processDropEvent };
